<script setup lang="ts">
import {
  claimLivePhotoPlayback,
  createLivePhotoRequestGuard,
  hasPointerMoved,
  releaseLivePhotoPlayback,
  resolveLivePhotoExitTiming,
  shouldInterruptLivePhotoTouch,
  shouldStartLivePhotoAfterLoad
} from '~/utils/livePhoto.mjs'

const props = defineProps<{
  photoSrc: string
  videoSrc: string
  hdrSources?: Array<{
    url: string
    type: string
  }>
  alt: string
}>()

const emit = defineEmits<{
  load: []
  error: []
}>()

const hoverDelay = 60
const touchHoldDelay = 80
const touchTapThreshold = 300
const touchMoveTolerance = 10
const mediaReadyTimeout = 8000
const zoomInDuration = 620
const zoomOutDuration = 400
const videoFadeDuration = 280
const videoBaseTransform = 'translate3d(0, 0, 0) scale(1)'

const videoElement = ref<HTMLVideoElement | null>(null)
const imageLoaded = ref(false)
const state = ref<'idle' | 'loading' | 'playing' | 'error'>('idle')
const videoVisible = ref(false)
const videoDissolved = ref(true)

const lifecycle = new AbortController()
let metadataPromise: Promise<HTMLVideoElement | null> | null = null
let hoverTimer = 0
let touchTimer = 0
let frameCallback = 0
let zoomAnimation: Animation | null = null
let blendAnimation: Animation | null = null
let exitGeneration = 0
const playbackRequests = createLivePhotoRequestGuard()
let touchPointerID = -1
let touchStartX = 0
let touchStartY = 0
let badgeTouchStartTime = 0
let pointerInside = false
let disposed = false

const clearTimers = () => {
  window.clearTimeout(hoverTimer)
  window.clearTimeout(touchTimer)
  hoverTimer = 0
  touchTimer = 0
}

const invalidateExit = () => {
  exitGeneration += 1
}

const cancelPresentedFrame = () => {
  const video = videoElement.value
  if (frameCallback && video?.cancelVideoFrameCallback) {
    video.cancelVideoFrameCallback(frameCallback)
  }
  frameCallback = 0
}

const rewindVideo = (video: HTMLVideoElement) => {
  if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
    return
  }
  if (video.currentTime > 0) {
    video.currentTime = 0
  }
}

const cancelVideoZoom = () => {
  zoomAnimation?.cancel()
  zoomAnimation = null
}

const cancelVideoBlend = () => {
  blendAnimation?.cancel()
  blendAnimation = null
}

const startVideoZoom = (video: HTMLVideoElement) => {
  cancelVideoZoom()
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return
  }
  zoomAnimation = video.animate(
    [
      { transform: videoBaseTransform },
      { transform: 'translate3d(0, 0, 0) scale(1.045)' }
    ],
    {
      duration: zoomInDuration,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards'
    }
  )
}

const isCurrentExit = (
  generation: number,
  zoom: Animation,
  dissolve: Animation
) => (
  !disposed &&
  generation === exitGeneration &&
  zoomAnimation === zoom &&
  blendAnimation === dissolve
)

const finishVideoZoom = async (video: HTMLVideoElement, generation: number) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    videoDissolved.value = true
    videoVisible.value = false
    rewindVideo(video)
    return
  }

  const zoomProgress = zoomAnimation?.effect?.getComputedTiming().progress
  const currentTransform = window.getComputedStyle(video).transform
  const currentOpacity = window.getComputedStyle(video).opacity
  const exitTiming = resolveLivePhotoExitTiming(
    zoomProgress,
    zoomOutDuration,
    videoFadeDuration
  )
  cancelVideoZoom()
  cancelVideoBlend()
  const animation = video.animate(
    [
      { transform: currentTransform === 'none' ? videoBaseTransform : currentTransform },
      { transform: videoBaseTransform }
    ],
    {
      duration: exitTiming.duration,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fill: 'forwards'
    }
  )
  zoomAnimation = animation
  const dissolve = video.animate(
    [
      { opacity: currentOpacity },
      { opacity: '0' }
    ],
    {
      delay: exitTiming.dissolveDelay,
      duration: exitTiming.dissolveDuration,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fill: 'both'
    }
  )
  blendAnimation = dissolve

  try {
    await Promise.all([animation.finished, dissolve.finished])
  } catch {
    return
  }
  if (!isCurrentExit(generation, animation, dissolve)) {
    return
  }

  videoDissolved.value = true
  videoVisible.value = false
  await nextTick()
  if (!isCurrentExit(generation, animation, dissolve)) {
    return
  }
  cancelVideoBlend()
  cancelVideoZoom()
  rewindVideo(video)
}

const stopPlayback = (immediate = false) => {
  clearTimers()
  invalidateExit()
  const generation = exitGeneration
  playbackRequests.cancel()
  cancelPresentedFrame()
  const video = videoElement.value
  if (video) {
    video.pause()
    if (immediate || !videoVisible.value) {
      videoDissolved.value = true
      cancelVideoBlend()
      cancelVideoZoom()
      videoVisible.value = false
      rewindVideo(video)
    } else {
      void finishVideoZoom(video, generation)
    }
  }
  if (state.value !== 'error') {
    state.value = 'idle'
  }
}

const finishPlayback = () => {
  stopPlayback()
  releaseLivePhotoPlayback(stopPlayback)
}

const resetPlayback = () => {
  stopPlayback(true)
  releaseLivePhotoPlayback(stopPlayback)
}

const failPlayback = () => {
  state.value = 'error'
  resetPlayback()
}

const waitForMetadata = () => {
  const video = videoElement.value
  if (!video || disposed || !props.videoSrc) {
    return Promise.resolve(null)
  }
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    return Promise.resolve(video)
  }
  if (metadataPromise) {
    return metadataPromise
  }

  const pending = new Promise<HTMLVideoElement | null>((resolve) => {
    let timeout = 0
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('loadedmetadata', handleReady)
      video.removeEventListener('error', handleFailure)
      lifecycle.signal.removeEventListener('abort', handleFailure)
    }
    const finish = (readyVideo: HTMLVideoElement | null) => {
      cleanup()
      resolve(readyVideo)
    }
    const handleReady = () => finish(video)
    const handleFailure = () => finish(null)

    video.addEventListener('loadedmetadata', handleReady, { once: true })
    video.addEventListener('error', handleFailure, { once: true })
    lifecycle.signal.addEventListener('abort', handleFailure, { once: true })
    video.src = props.videoSrc
    video.load()
    timeout = window.setTimeout(handleFailure, mediaReadyTimeout)
  })

  metadataPromise = pending
  void pending.finally(() => {
    if (metadataPromise === pending) {
      metadataPromise = null
    }
  })
  return pending
}

const waitForVideoStart = (video: HTMLVideoElement, request: number) => {
  if (video.currentTime === 0) {
    return Promise.resolve(true)
  }
  return new Promise<boolean>((resolve) => {
    let timeout = 0
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('error', handleFailure)
      lifecycle.signal.removeEventListener('abort', handleFailure)
    }
    const finish = (ready: boolean) => {
      cleanup()
      resolve(ready && playbackRequests.isCurrent(request) && !disposed)
    }
    const handleSeeked = () => finish(true)
    const handleFailure = () => finish(false)

    video.addEventListener('seeked', handleSeeked, { once: true })
    video.addEventListener('error', handleFailure, { once: true })
    lifecycle.signal.addEventListener('abort', handleFailure, { once: true })
    video.currentTime = 0
    timeout = window.setTimeout(handleFailure, mediaReadyTimeout)
  })
}

const revealFirstFrame = (video: HTMLVideoElement, request: number) => {
  cancelPresentedFrame()
  const reveal = () => {
    frameCallback = 0
    if (!disposed && playbackRequests.isCurrent(request) && !video.paused) {
      videoVisible.value = true
      startVideoZoom(video)
      state.value = 'playing'
    }
  }
  if (video.requestVideoFrameCallback) {
    frameCallback = video.requestVideoFrameCallback(reveal)
    return
  }
  requestAnimationFrame(reveal)
}

const startPlayback = async () => {
  if (!imageLoaded.value || state.value === 'loading' || state.value === 'playing') {
    return
  }
  const request = playbackRequests.begin()
  invalidateExit()
  cancelVideoBlend()
  cancelVideoZoom()
  state.value = 'loading'
  videoDissolved.value = false
  videoVisible.value = false
  claimLivePhotoPlayback(stopPlayback)

  const video = await waitForMetadata()
  if (!video || disposed || !playbackRequests.isCurrent(request)) {
    if (playbackRequests.isCurrent(request)) {
      failPlayback()
    }
    return
  }
  if (!await waitForVideoStart(video, request)) {
    if (playbackRequests.isCurrent(request)) {
      failPlayback()
    }
    return
  }
  try {
    await video.play()
  } catch {
    if (playbackRequests.isCurrent(request)) {
      failPlayback()
    }
    return
  }
  if (disposed || !playbackRequests.isCurrent(request)) {
    video.pause()
    return
  }
  revealFirstFrame(video, request)
}

const handleImageLoad = () => {
  imageLoaded.value = true
  emit('load')
  if (shouldStartLivePhotoAfterLoad(pointerInside, hoverTimer)) {
    void startPlayback()
  }
}

const handleMouseEnter = () => {
  if (
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return
  }
  pointerInside = true
  window.clearTimeout(hoverTimer)
  hoverTimer = window.setTimeout(() => {
    hoverTimer = 0
    if (pointerInside) {
      void startPlayback()
    }
  }, hoverDelay)
}

const handleMouseLeave = () => {
  pointerInside = false
  finishPlayback()
}

const handlePointerDown = (event: PointerEvent) => {
  badgeTouchStartTime = 0
  if (event.pointerType === 'mouse' || !imageLoaded.value) {
    return
  }
  const startedOnBadge = event.target instanceof Element &&
    Boolean(event.target.closest('.live-photo-badge'))
  if (startedOnBadge) {
    badgeTouchStartTime = event.timeStamp
  }
  event.stopPropagation()
  touchPointerID = event.pointerId
  touchStartX = event.clientX
  touchStartY = event.clientY
  window.clearTimeout(touchTimer)
  touchTimer = window.setTimeout(() => {
    touchTimer = 0
    if (touchPointerID === event.pointerId) {
      void startPlayback()
    }
  }, touchHoldDelay)
}

const handlePointerMove = (event: PointerEvent) => {
  if (event.pointerId !== touchPointerID) {
    return
  }
  if (hasPointerMoved(
    touchStartX,
    touchStartY,
    event.clientX,
    event.clientY,
    touchMoveTolerance
  ) && shouldInterruptLivePhotoTouch(state.value)) {
    touchPointerID = -1
    finishPlayback()
  }
}

const finishPointer = (event: PointerEvent) => {
  if (event.pointerId !== touchPointerID) {
    return
  }
  touchPointerID = -1
  finishPlayback()
}

const handlePointerCancel = (event: PointerEvent) => {
  if (event.pointerId !== touchPointerID) {
    return
  }
  touchPointerID = -1
  badgeTouchStartTime = 0
  if (shouldInterruptLivePhotoTouch(state.value)) {
    finishPlayback()
  }
}

// handleBadgeClick 播放 Live Photo，并忽略长按结束后派生的点击事件。
const handleBadgeClick = (event: MouseEvent) => {
  const touchDuration = badgeTouchStartTime > 0
    ? event.timeStamp - badgeTouchStartTime
    : 0
  badgeTouchStartTime = 0
  if (event.detail > 0 && touchDuration >= touchTapThreshold) {
    return
  }
  void startPlayback()
}

watch(
  [() => props.photoSrc, () => props.videoSrc],
  ([photoSrc, videoSrc], [previousPhotoSrc, previousVideoSrc]) => {
    resetPlayback()
    if (photoSrc !== previousPhotoSrc) {
      imageLoaded.value = false
    }
    if (videoSrc !== previousVideoSrc) {
      metadataPromise = null
      state.value = 'idle'
      const video = videoElement.value
      if (video) {
        video.removeAttribute('src')
        video.load()
      }
    }
  }
)

onBeforeUnmount(() => {
  disposed = true
  lifecycle.abort()
  pointerInside = false
  touchPointerID = -1
  resetPlayback()
  const video = videoElement.value
  if (video) {
    video.removeAttribute('src')
    video.load()
  }
  metadataPromise = null
})
</script>

<template>
  <div
    class="live-photo"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="finishPointer"
    @pointercancel="handlePointerCancel"
  >
    <div class="live-photo-media">
      <BlogMediaImage
        :src="photoSrc"
        :fallback-src="photoSrc"
        :sources="hdrSources"
        :alt="alt"
        loading="eager"
        :progressive="false"
        @load="handleImageLoad"
        @error="emit('error')"
      />
      <video
        ref="videoElement"
        class="live-photo-video"
        :class="{
          'is-visible': videoVisible,
          'is-dissolved': videoDissolved
        }"
        muted
        playsinline
        preload="metadata"
        aria-hidden="true"
        @ended="finishPlayback"
        @error="failPlayback"
      />
    </div>
    <button
      type="button"
      class="live-photo-badge"
      aria-label="播放 Live Photo"
      @keydown.enter.stop
      @keydown.space.stop
      @click.stop="handleBadgeClick"
    >
      <svg class="live-photo-icon" viewBox="0 0 20 20">
        <use xlink:href="#livephoto" />
      </svg>
      LIVE
    </button>
    <slot />
  </div>
</template>

<style scoped>
.live-photo {
  display: block;
  width: 100%;
}

.live-photo {
  position: relative;
  overflow: hidden;
  -webkit-touch-callout: none;
  user-select: none;
  touch-action: pan-y;
}

.live-photo-media {
  position: relative;
  width: 100%;
}

.live-photo-video {
  position: absolute;
  z-index: 1;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transform: translate3d(0, 0, 0) scale(1);
  transform-origin: center;
  backface-visibility: hidden;
  pointer-events: none;
  transition: opacity 280ms cubic-bezier(0.33, 1, 0.68, 1);
}

.live-photo-video.is-visible {
  opacity: 1;
}

.live-photo-video.is-dissolved {
  visibility: hidden;
  opacity: 0;
  transition: none;
}

.live-photo-badge {
  position: absolute;
  z-index: 3;
  top: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 1px;
  border-radius: 999px;
  border: 0;
  background: rgba(255, 255, 255, 0.18);
  -webkit-backdrop-filter: blur(6px) saturate(1.15);
  backdrop-filter: blur(6px) saturate(1.15);
  color: rgba(255, 255, 255, 0.94);
  font-family: inherit;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.02em;
  cursor: pointer;
}

.live-photo-icon {
  display: block;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  overflow: visible;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.36));
}

@media (prefers-reduced-motion: reduce) {
  .live-photo-video {
    transition: none;
  }
}
</style>
